import React from 'react';
import { toast } from '@/hooks/use-toast';
import { ErrorToast } from '@/components/ErrorToast';
import { ErrResp, ErrorDetails, ERROR_TAXONOMY, createFallbackError } from '@/lib/errors';

/**
 * Unified error handling utilities for consistent error display across the app
 */

interface ShowErrorOptions {
  onRetry?: () => void;
  className?: string;
}

interface SupabaseInvokeOptions {
  body?: unknown;
  headers?: Record<string, string>;
  [key: string]: unknown;
}

interface SupabaseFunctionsClient {
  functions: {
    invoke(
      functionName: string,
      options?: SupabaseInvokeOptions,
    ): Promise<{ data: unknown; error: unknown }>;
  };
}

/**
 * Display an error from an edge function API response
 */
export function showApiError(
  resp: ErrResp | null | undefined, 
  fallback: ErrorDetails,
  options: ShowErrorOptions = {}
) {
  const { error, request_id, details } = resp ?? {};
  const errorDetails = error ?? fallback;
  
  const title = `${errorDetails.name} (${errorDetails.code})`;
  
  toast({
    title: "Error",
    description: React.createElement(ErrorToast, {
      title,
      message: errorDetails.message,
      requestId: request_id,
      details,
      onRetry: options.onRetry,
      className: options.className,
    }),
    variant: "destructive",
    duration: 8000, // Longer duration for error messages
  });
}

/**
 * Display a network/connectivity error
 */
export function showNetworkError(options: ShowErrorOptions = {}) {
  const error = ERROR_TAXONOMY.NET.OFFLINE;
  const title = `${error.name} (${error.code})`;
  
  toast({
    title: "Connection Error",
    description: React.createElement(ErrorToast, {
      title,
      message: error.message,
      onRetry: options.onRetry,
      className: options.className,
    }),
    variant: "destructive",
    duration: 6000,
  });
}

/**
 * Display a generic unexpected error
 */
export function showUnexpectedError(
  message?: string, 
  details?: unknown,
  options: ShowErrorOptions = {}
) {
  const error = createFallbackError(message);
  const title = `${error.name} (${error.code})`;
  
  toast({
    title: "Unexpected Error",
    description: React.createElement(ErrorToast, {
      title,
      message: error.message,
      details,
      onRetry: options.onRetry,
      className: options.className,
    }),
    variant: "destructive",
    duration: 8000,
  });
}

/**
 * Check if a response is an error response
 */
export function isErrorResponse(resp: unknown): resp is ErrResp {
  if (!resp || typeof resp !== 'object') {
    return false;
  }
  const candidate = resp as Partial<ErrResp> & Record<string, unknown>;
  return candidate.ok === false && 'error' in candidate && candidate.error != null;
}

/**
 * Check if error is a network/fetch error
 */
export function isNetworkError(error: unknown): boolean {
  const errorStr = String(error);
  return (
    errorStr.includes('fetch') ||
    errorStr.includes('NetworkError') ||
    errorStr.includes('Failed to fetch') ||
    errorStr.includes('ERR_NETWORK') ||
    errorStr.includes('ERR_INTERNET_DISCONNECTED')
  );
}

/**
 * Wrapper for supabase.functions.invoke with consistent error handling
 */
export async function invokeWithErrorHandling<T = unknown>(
  supabase: SupabaseFunctionsClient,
  functionName: string,
  options: SupabaseInvokeOptions = {},
  context?: string
): Promise<{ data: T | null; error: ErrResp | null }> {
  try {
    const { data, error: supabaseError } = await supabase.functions.invoke(functionName, options);
    
    if (supabaseError) {
      throw supabaseError;
    }
    
    // Check if the response indicates an error
    if (isErrorResponse(data)) {
      return { data: null, error: data };
    }
    
    const normalizedData =
      data && typeof data === 'object' && data !== null && 'data' in data
        ? (data as { data: unknown }).data
        : data;

    return { data: (normalizedData as T) ?? null, error: null };
  } catch (error) {
    // Handle network errors or other exceptions
    const detailsPayload =
      import.meta.env.MODE === 'development'
        ? { raw: String(error), ...(context ? { context } : {}) }
        : context
        ? { context }
        : undefined;

    const fallbackError: ErrResp = {
      ok: false,
      error: isNetworkError(error) 
        ? ERROR_TAXONOMY.NET.OFFLINE
        : createFallbackError(String(error)),
      request_id: crypto.randomUUID(),
      details: detailsPayload
    };
    
    return { data: null, error: fallbackError };
  }
}

/**
 * Show error with automatic retry for network errors
 */
export function showErrorWithRetry(
  error: ErrResp | null | undefined,
  fallback: ErrorDetails,
  retryFn: () => void
) {
  const isNetErr = error?.error?.code === 'NET.OFFLINE';
  
  showApiError(error, fallback, {
    onRetry: isNetErr ? retryFn : undefined
  });
}

/**
 * Context-specific error fallbacks for different parts of the app
 */
export const ERROR_CONTEXTS = {
  demo: ERROR_TAXONOMY.DEMO.CREATE_FAILED,
  order: ERROR_TAXONOMY.ORDER.CREATE_FAILED,
  pos: ERROR_TAXONOMY.POS.MENU_LOAD_FAILED,
  qr: ERROR_TAXONOMY.QR.TABLE_UNKNOWN,
  payment: ERROR_TAXONOMY.PAY.INIT_FAILED,
  auth: ERROR_TAXONOMY.AUTH.NOT_SIGNED_IN,
} as const;
